import {
  Button,
  AccordionSummary,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import axiosInstance from "../../axiosConfig";

import TaskChips from "./TaskChips";
import {
  RenderIf,
  PrivacyLevelsSelector,
  EditableCategories,
  EditableSummary,
} from "@adl/smart-ui-library";
import TaskHeader from "./TaskHeader";
import { useAppSelector } from "@/hooks";
import {
  selectDocumentCatagories,
  selectDocumentPrivacyLevels,
} from "@/redux/casesSlice";
import * as Styled from "./TaskCard.styles";
import {
  IVerifyTaskPayload,
  ITask,
  IDocumentPrivacy,
} from "@/redux/store.types";
import FileUploadModal from "../FileUploader/FileUploadModal";
import QuickApprovalModal from "../FileUploader/QuickApprovalModal";

type Props = {
  task: ITask;
  handleVerify?: (payload: IVerifyTaskPayload) => void;
  onRerender?: (payload: boolean) => void;
  data?: any;
};

const TaskCard = ({ task, onRerender, data }: Props) => {
  const [summary, setSummary] = React.useState<string | null>(
    task?.document?.summary
  );
  const [modalOpen, setModalOpen] = React.useState(false);
  const [approvalModalOpen, setApprovalModalOpen] = React.useState(false);
  const [categoryId, setCategoryId] = React.useState<string | number>(
    task?.document?.categoryId
  );
  const [privacyLevels, setPrivacyLevels] = React.useState<IDocumentPrivacy[]>(
    task?.document?.privacyLevels
  );

  const categories = useAppSelector(selectDocumentCatagories);
  const levels = useAppSelector(selectDocumentPrivacyLevels);

  const [metadata, setMetadata] = useState({});
  const [downloadInProgress, setDownloadInProgress] = useState(false);

  const onDownload = async () => {
    setDownloadInProgress(true);
    try {
      const response = await axiosInstance.get(
        `documents/downloads/${task.deliveryEngagementId}/${task.document.documentId}/${task.taskId}`,
        {
          responseType: "arraybuffer",
        }
      );

      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });

      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = task.document.fileNameOriginal;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      onRerender && onRerender(true);
      setDownloadInProgress(false);
    } catch (error) {
      console.error("Download error:", error);
    }
  };

  const onUpload = () => {
    setModalOpen(true);
  };

  const onApproval = () => {
    setApprovalModalOpen(true);
  };

  const handleCancel = () => {
    setModalOpen(false);
    setApprovalModalOpen(false);
    onRerender && onRerender(true);
  };
  useEffect(() => {
    setSummary(task?.document?.summary);
    setCategoryId(task?.document?.categoryId);
    setPrivacyLevels(task?.document?.privacyLevels);
    setMetadata({
      deliveryEngagementId: task?.deliveryEngagementId,
      documentId: task?.document.documentId,
      taskId: task?.taskId,
    });
  }, [task]);

  return (
    <>
      <Styled.TaskAccordion>
        <AccordionSummary aria-controls="panel1-content" id="panel1-header">
          <TaskHeader task={task} />
        </AccordionSummary>
        <Styled.TaskAccordionDetails>
          <RenderIf isTrue={downloadInProgress}>
            <Box
              sx={{
                position: "absolute",
                zIndex: 10,
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <CircularProgress />
                <Typography variant="body1">Processing...</Typography>
              </Box>
            </Box>
          </RenderIf>
          <Typography variant="h6">Overview</Typography>
          <TaskChips task={task} data={data} />
          <RenderIf
            isTrue={Boolean(task.taskStatusType === 4 && task.rejectReason)}
          >
            <Typography variant="h6">Reject reason</Typography>
            <span>{task.rejectReason}</span>
          </RenderIf>
          <RenderIf isTrue={false}>
            <Typography variant="h6">File Category</Typography>
            <EditableCategories
              value={categoryId}
              onUpdate={(value: { id: string | number; name: string }) => {
                setCategoryId(value.id);
              }}
              disabled={task?.verified}
              categories={categories}
            />

            <Typography variant="h6">Privacy Level</Typography>
            <PrivacyLevelsSelector
              values={privacyLevels}
              onUpdate={(value: IDocumentPrivacy[]) => {
                setPrivacyLevels(value);
              }}
              disabled={task?.verified}
              levels={levels}
            />
            <EditableSummary
              text={summary || ""}
              onUpdate={(text: string) => {
                if (text === summary) return;
                setSummary(text);
              }}
              disabled={task?.verified}
            />
          </RenderIf>

          <Box sx={{ display: "flex", gap: "10px" }}>
            <RenderIf
              isTrue={task.taskStatusType === 3 || task.taskStatusType === 4}
            >
              <Button
                data-testid="verify-button"
                variant="contained"
                disableElevation
                disabled={downloadInProgress}
                onClick={onDownload}
              >
                Download
              </Button>
            </RenderIf>

            <RenderIf isTrue={task.taskStatusType === 4}>
              <Button
                data-testid="verify-button"
                variant="contained"
                disabled={downloadInProgress}
                disableElevation
                onClick={onUpload}
              >
                Upload
              </Button>
            </RenderIf>

            <RenderIf
              isTrue={task.taskStatusType === 3 || task.taskStatusType === 4}
            >
              <Button
                data-testid="verify-button"
                variant="contained"
                disabled={downloadInProgress}
                disableElevation
                onClick={onApproval}
              >
                Quick Sanitise
              </Button>
            </RenderIf>
          </Box>
          <RenderIf isTrue={modalOpen}>
            <FileUploadModal onCancel={handleCancel} metadata={metadata} />
          </RenderIf>
          <RenderIf isTrue={approvalModalOpen}>
            <QuickApprovalModal onCancel={handleCancel} metadata={metadata} />
          </RenderIf>
        </Styled.TaskAccordionDetails>
      </Styled.TaskAccordion>
    </>
  );
};

export default TaskCard;
